import { useMemo } from 'react';

import { useExperimentMultipleFlagsExposed } from '@zenbusiness/application-commons-experiments';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

// Control variation is the default variation that should not override the config
const CONTROL_VARIATION = 'control';

/** @typedef {import('../config/FlowConfigBase').FlowConfigOptions} FlowConfigOptions */
/** @typedef {import('../config/FlowConfigBase').FeatureFlagConfig} FeatureFlagConfig */

/**
 * Custom hook to update the flow configuration based on multiple feature flags.
 * This hook evaluates all feature flags defined in the flow config and applies
 * their variations according to their merge configurations.
 *
 * @param {FlowConfigOptions} flowConfig - The initial configuration object for the flow.
 * @returns {FlowConfigOptions} An object containing the updated configuration with all active feature flag variations applied
 */
function useFlowConfigMultipleExperiments(flowConfig) {
  const { featureFlags } = flowConfig;
  const captureException = useCaptureException();

  // Define merge type priorities, lower number = higher priority
  const MERGE_TYPE_PRIORITY = {
    replace: 0, // Process replace operations first
    insert: 1, // Process insert operations second
    UNKNOWN: Infinity // Process unknown/invalid types last
  };

  // Get all feature flag keys from the featureFlags object
  const featureFlagKeys = useMemo(
    () => (featureFlags ? Object.keys(featureFlags) : []),
    [featureFlags]
  );

  // Group and sort feature flags by merge type
  const sortedFeatureFlagKeys = useMemo(() => {
    if (!featureFlags) return [];

    return Object.entries(featureFlags)
      .sort(([, a], [, b]) => {
        // Get priority for each flag's merge type
        // If type is not 'replace' or 'insert', use UNKNOWN priority
        const aTypePriority = MERGE_TYPE_PRIORITY[a?.merge?.type] ?? MERGE_TYPE_PRIORITY.UNKNOWN;
        const bTypePriority = MERGE_TYPE_PRIORITY[b?.merge?.type] ?? MERGE_TYPE_PRIORITY.UNKNOWN;

        // Lower priority number will come first
        return aTypePriority - bTypePriority;
      })
      .map(([key]) => key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags]);

  // Prepare flag configurations for useExperimentMultipleFlagsExposed
  const flagConfigs = useMemo(
    () =>
      sortedFeatureFlagKeys.map((flagKey) => ({
        flag: flagKey,
        defaultValue: null,
        withLoadingFlag: false,
        skipEvaluation: false
      })),
    [sortedFeatureFlagKeys]
  );

  // Use the new hook to evaluate all feature flags and track experiment exposure
  const flagsEvaluationResults = useExperimentMultipleFlagsExposed(flagConfigs);

  const config = useMemo(() => {
    if (!featureFlags || !featureFlagKeys.length) {
      return flowConfig;
    }

    let updatedConfig = { ...flowConfig };

    // Iterate through feature flags in sorted order
    sortedFeatureFlagKeys.forEach((flagKey) => {
      const result = flagsEvaluationResults[flagKey];
      const flagConfig = featureFlags[flagKey];
      const { variations: configuredVariations, merge } = flagConfig || {};
      const variationKey = result?.value;

      // Skip configuration if the variation is not configured or is the control variation
      if (!configuredVariations || !variationKey || variationKey === CONTROL_VARIATION) {
        return;
      }

      const variation = configuredVariations[variationKey];

      if (!variation || !Object.keys(variation).length) {
        captureException(
          `Variation key "${variationKey}" not configured in flow config for the feature flag "${flagKey}".`,
          {
            level: 'warning',
            contexts: {
              featureFlag: {
                flagKey,
                variationKey
              }
            }
          }
        );
        return;
      }

      // Apply the variation based on the merge configuration
      if (merge?.type === 'insert') {
        const { inKey, afterValue } = merge;

        if (!inKey || !afterValue) {
          captureException(
            `Invalid merge configuration for feature flag "${flagKey}". Insert type requires inKey and afterValue.`,
            {
              level: 'warning',
              contexts: {
                featureFlag: {
                  flagKey,
                  merge
                }
              }
            }
          );
          return;
        }

        // Validate that inKey is 'pageKeys'
        if (inKey !== 'pageKeys') {
          captureException(
            `Invalid inKey "${inKey}" in feature flag "${flagKey}". Only 'pageKeys' is allowed for insert merge type.`,
            {
              level: 'warning',
              contexts: {
                featureFlag: {
                  flagKey,
                  inKey,
                  mergeType: 'insert'
                }
              }
            }
          );
          return;
        }

        // Get the current array from the config
        const currentArray = updatedConfig[inKey] || [];

        // Find the index of the afterValue
        const insertIndex = currentArray.indexOf(afterValue);

        if (insertIndex === -1) {
          captureException(
            `Could not find afterValue "${afterValue}" in ${inKey} for feature flag "${flagKey}".`,
            {
              level: 'warning',
              contexts: {
                featureFlag: {
                  flagKey,
                  afterValue,
                  inKey
                }
              }
            }
          );
          return;
        }

        // Insert the variation values after the specified value
        const newArray = [
          ...currentArray.slice(0, insertIndex + 1),
          ...(Array.isArray(variation) ? variation : [variation]),
          ...currentArray.slice(insertIndex + 1)
        ];

        updatedConfig = {
          ...updatedConfig,
          [inKey]: newArray
        };
      } else {
        // Default to replace behavior
        updatedConfig = {
          ...updatedConfig,
          ...(typeof variation === 'object' ? variation : { [flagKey]: variation })
        };
      }
    });

    return updatedConfig;
  }, [
    captureException,
    featureFlagKeys,
    flagsEvaluationResults,
    featureFlags,
    flowConfig,
    sortedFeatureFlagKeys
  ]);

  return config;
}

export default useFlowConfigMultipleExperiments;
